import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

/** Záložky detailu turnaje v administraci (spec 17.10). Play-off přibude v etapě B. */
enum class TournamentTab(val label: String) {
    SETTINGS("Nastavení"),
    DESCRIPTION("Popis"),
    SLOTS("Obsazovačka"),
    ENTRIES("Účastníci"),
    GROUPS("Rozdělení do skupin"),
    SCORES("Výsledky"),
}

/**
 * Vše, co záložky detailu sdílejí — načte se jednou při otevření turnaje (a po každé změně), místo
 * aby si každá záložka tahala účastníky/soupisky zvlášť.
 */
class TournamentAdminData(
    val grouping: TournamentGroupingDto? = null,
    val groupPlayers: List<TournamentGroupPlayerDto> = emptyList(),
    val entries: List<TournamentEntryDto> = emptyList(),
    val slots: List<TournamentSlotDto> = emptyList(),
    val scores: List<TournamentScoreDto> = emptyList(),
    /** Hráči se soupiskou v sezóně turnaje — z nich se vybírají „ligoví" účastníci. */
    val rosterPlayers: List<PlayerDto> = emptyList(),
    /** playerId -> název týmu v téhle sezóně; kdo tu není, je host (spec 17.8). */
    val teamByPlayer: Map<String, String> = emptyMap(),
    /** Všichni hráči v evidenci — z nich se vybírá host (hráč bez soupisky v téhle sezóně). */
    val allPlayers: List<PlayerDto> = emptyList(),
)

/**
 * Detail turnaje v administraci. Data se drží tady a záložky je jen zobrazují, takže po uložení
 * kdekoliv stačí jedno [reload] a všechny záložky vidí to samé.
 */
@Composable
fun TournamentAdminScreen(
    token: String?,
    role: String?,
    seasonId: String,
    tournament: TournamentDto,
    onChanged: () -> Unit,
    onBack: () -> Unit,
) {
    var current by remember(tournament.id) { mutableStateOf(tournament) }
    var tab by remember(tournament.id) { mutableStateOf(TournamentTab.SETTINGS) }
    var data by remember(tournament.id) { mutableStateOf(TournamentAdminData()) }
    var loading by remember(tournament.id) { mutableStateOf(true) }
    var error by remember(tournament.id) { mutableStateOf<String?>(null) }
    var reloadKey by remember(tournament.id) { mutableStateOf(0) }

    LaunchedEffect(tournament.id, reloadKey) {
        // Keep tab composition and its open slot / staged selection during background reloads.
        if (reloadKey == 0) loading = true
        try {
            val entries = Api.fetchTournamentEntries(token, current.id)
            val slots = Api.fetchTournamentSlots(token, current.id)
            val scores = Api.fetchTournamentScores(token, current.id)
            val roster = Api.fetchRosterForSeason(token, seasonId)
            val seasonTeams = Api.fetchAllTeamsForSeason(token, seasonId)
            val teamNameById = seasonTeams.associate { slt ->
                slt.teamId to (slt.teamNameOverride ?: slt.team?.name ?: "")
            }
            data = TournamentAdminData(
                grouping = Api.fetchTournamentGrouping(token, current.id),
                groupPlayers = Api.fetchTournamentGroupPlayers(token, current.id),
                entries = entries,
                slots = slots,
                scores = scores,
                rosterPlayers = roster.mapNotNull { it.player }.sortedBy { it.name.lowercase() },
                teamByPlayer = roster.associate { it.playerId to (teamNameById[it.teamId] ?: "") },
                allPlayers = Api.fetchAllPlayers(token).sortedBy { it.name.lowercase() },
            )
            error = null
        } catch (e: Throwable) {
            error = "Načtení turnaje selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val reload: () -> Unit = { reloadKey++; onChanged() }

    Div(attrs = { classes("card", "tournament-header") }) {
        Div(attrs = { classes("actions", "tournament-tabs") }) {
            Button(attrs = { onClick { onBack() } }) { Text("← Zpět na turnaje") }
        }
        H2 { Text("Turnaj: ${current.title}") }
        P(attrs = { classes("muted") }) {
            Text("${tournamentTypeLabel(current.type)} · ${tournamentStatusLabel(current.status)} · ${formatDate(current.startsAt)} · /${current.slug}")
        }
        if (current.isDraft) {
            P(attrs = { classes("muted") }) { Text("Koncept — veřejně se turnaj nezobrazuje, vidí ho jen administrátoři.") }
        }
        error?.let { Div(attrs = { classes("editor-error") }) { Text("Chyba: $it") } }

        Div(attrs = { classes("actions") }) {
            TournamentTab.entries.forEach { t ->
                NavButton(t.label, tab == t) { tab = t }
            }
        }

        if (loading) {
            Text("Načítám…")
        } else {
            when (tab) {
                TournamentTab.SETTINGS -> TournamentSettingsTab(
                    token = token, tournament = current, canEdit = role.canManage(),
                    scores = data.scores,
                    onSaved = { updated -> current = updated; onChanged() },
                )
                TournamentTab.DESCRIPTION -> TournamentDescriptionTab(
                    token = token, tournament = current, canEdit = role.canManage(),
                    onSaved = { html -> current = current.copy(bodyHtml = html); onChanged() },
                )
                TournamentTab.SLOTS -> TournamentSlotsTab(
                    token = token, tournament = current, data = data,
                    canEdit = role.canManage(), onReload = reload,
                )
                TournamentTab.ENTRIES -> TournamentEntriesTab(
                    token = token, tournament = current, data = data,
                    canEdit = role.canManage(), onReload = reload,
                )
                TournamentTab.GROUPS -> TournamentGroupsTab(
                    token = token, tournament = current, grouping = data.grouping, players = data.groupPlayers,
                    canEdit = role.canManage(), onReload = reload,
                )
                TournamentTab.SCORES -> TournamentScoresTab(
                    token = token, tournament = current, data = data,
                    canEdit = role.canManage(), onReload = reload,
                )
            }
        }
    }
}

/**
 * Nastavení turnaje. Typ se nemění (spec 17.1) — jen se zobrazuje. Parametry se ukládají do
 * `config` jen v podobě klíčů platných pro daný typ (viz [tournamentConfigJson]).
 */
@Composable
private fun TournamentSettingsTab(
    token: String?,
    tournament: TournamentDto,
    canEdit: Boolean,
    scores: List<TournamentScoreDto>,
    onSaved: (TournamentDto) -> Unit,
) {
    var title by remember(tournament.id) { mutableStateOf(tournament.title) }
    var slug by remember(tournament.id) { mutableStateOf(tournament.slug) }
    var status by remember(tournament.id) { mutableStateOf(tournament.status) }
    var startsAt by remember(tournament.id) { mutableStateOf(tournament.startsAt ?: "") }
    var endsAt by remember(tournament.id) { mutableStateOf(tournament.endsAt ?: tournament.startsAt ?: "") }
    var config by remember(tournament.id) { mutableStateOf(tournament.config) }
    var saving by remember(tournament.id) { mutableStateOf(false) }
    var saved by remember(tournament.id) { mutableStateOf(false) }
    var error by remember(tournament.id) { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    val minimumGames = scores.filter { it.phase == TOURNAMENT_PHASE_QUALIFICATION }.maxOfOrNull { it.gameNo } ?: 1
    val configuredGames = when (tournament.type) {
        TOURNAMENT_BRACKET -> config.qualGames ?: 3
        TOURNAMENT_BONUS -> config.games ?: 4
        else -> config.games ?: 1
    }

    Div(attrs = { classes("tournament-settings") }) {
        H3 { Text("Nastavení") }
        Div(attrs = { classes("tournament-setting-row") }) {
            Label(attrs = { attr("for", "tournament-title") }) { Text("Název:") }
            Input(type = InputType.Text) {
                id("tournament-title")
                if (!canEdit) disabled()
                value(title)
                onInput { title = it.value; saved = false }
            }
        }
        Div(attrs = { classes("tournament-setting-row") }) {
            Label(attrs = { attr("for", "tournament-slug") }) { Text("Adresa (slug):") }
            Input(type = InputType.Text) {
                id("tournament-slug")
                if (!canEdit) disabled()
                value(slug)
                onInput { slug = slugify(it.value); saved = false }
            }
        }
        Div(attrs = { classes("tournament-setting-row") }) {
            Span(attrs = { classes("tournament-setting-label") }) { Text("Typ:") }
            Span(attrs = { classes("tournament-setting-value") }) { Text(tournamentTypeLabel(tournament.type)) }
        }
        Div(attrs = { classes("tournament-setting-row") }) {
            Label(attrs = { attr("for", "tournament-status") }) { Text("Stav:") }
            Select(attrs = {
                id("tournament-status")
                if (!canEdit) disabled()
                onChange { status = it.value ?: status; saved = false }
            }) {
                TOURNAMENT_STATUSES.forEach { (value, label) ->
                    Option(value, attrs = { if (value == status) selected() }) { Text(label) }
                }
            }
        }
        Div(attrs = { classes("tournament-setting-row") }) {
            Label(attrs = { attr("for", "tournament-start") }) { Text("Od:") }
            Input(type = InputType.Date) {
                id("tournament-start")
                if (!canEdit) disabled()
                value(startsAt)
                onInput {
                    val wasSameAsStart = endsAt.isBlank() || endsAt == startsAt
                    startsAt = it.value
                    if (wasSameAsStart) endsAt = it.value
                    saved = false
                }
            }
        }
        Div(attrs = { classes("tournament-setting-row") }) {
            Label(attrs = { attr("for", "tournament-end") }) { Text("Do:") }
            Input(type = InputType.Date) {
                id("tournament-end")
                if (!canEdit) disabled()
                value(endsAt)
                onInput { endsAt = it.value; saved = false }
            }
        }

        CheckField("Zobrazit rozdělení do skupin ve veřejném přehledu", config.showGroups, canEdit && !saving) {
            config = config.copy(showGroups = it); saved = false
        }
        P(attrs = { classes("muted") }) {
            Text("Skrytí odstraní veřejnou záložku rozdělení. Skupiny hráčů i výsledky zůstanou uložené a dostupné v administraci.")
        }

        H4 { Text("Parametry") }
        when (tournament.type) {
            TOURNAMENT_BRACKET -> {
                NumberField("Her v kvalifikaci", config.qualGames ?: 3, canEdit) {
                    config = config.copy(qualGames = it); saved = false
                }
                NumberField("Postupuje mužů do play-off", config.poMen ?: 24, canEdit) {
                    config = config.copy(poMen = it); saved = false
                }
                NumberField("Postupuje žen do play-off", config.poWomen ?: 16, canEdit) {
                    config = config.copy(poWomen = it); saved = false
                }
                NumberField("Her na duel play-off", config.poGamesPerMatch ?: 2, canEdit) {
                    config = config.copy(poGamesPerMatch = it); saved = false
                }
                CheckField("Bracket reset ve finále", config.bracketReset ?: true, canEdit) {
                    config = config.copy(bracketReset = it); saved = false
                }
                CheckField("Hraje se o 3. místo", config.thirdPlaceMatch ?: false, canEdit) {
                    config = config.copy(thirdPlaceMatch = it); saved = false
                }
                P(attrs = { classes("muted") }) {
                    Text("Play-off (pavouk s dvojitou eliminací) je etapa B — zatím se zadává jen kvalifikace.")
                }
            }
            TOURNAMENT_BONUS -> {
                NumberField("Počet her", config.games ?: 4, canEdit) {
                    config = config.copy(games = it); saved = false
                }
                NumberField("Bonifikace (% z označené hry)", config.bonusPct ?: 50, canEdit) {
                    config = config.copy(bonusPct = it); saved = false
                }
                P(attrs = { classes("muted") }) {
                    Text("Rozdělení všech hráčů a uložené průměry najdete na záložce Rozdělení do skupin.")
                }
            }
            else -> NumberField("Počet her", config.games ?: 3, canEdit) {
                config = config.copy(games = it); saved = false
            }
        }
        P(attrs = { classes("muted") }) {
            Text("Počet her nelze snížit pod nejvyšší hru s uloženým výsledkem (aktuální minimum: $minimumGames).")
        }
        if (configuredGames < minimumGames) P(attrs = { classes("editor-error") }) {
            Text("Nelze uložit méně než $minimumGames her — hry navíc už mají zadané výsledky.")
        }

        error?.let { Div(attrs = { classes("editor-error") }) { Text("Chyba: $it") } }
        if (saved) Div { Text("Uloženo.") }

        if (canEdit) {
            Div(attrs = { classes("save-action") }) {
                Button(attrs = {
                    if (saving || configuredGames < minimumGames || title.isBlank() || slug.isBlank() || (endsAt.isNotBlank() && startsAt.isBlank()) ||
                        (startsAt.isNotBlank() && endsAt.isNotBlank() && endsAt < startsAt)) disabled()
                    onClick {
                        saving = true
                        error = null
                        saved = false
                        scope.launch {
                            try {
                                val input = TournamentInput(
                                    seasonId = tournament.seasonId,
                                    title = title.trim(),
                                    slug = slug,
                                    type = tournament.type,
                                    status = status,
                                    startsAt = startsAt.ifBlank { null },
                                    endsAt = endsAt.takeIf { it.isNotBlank() },
                                    bodyHtml = tournament.bodyHtml,
                                    config = tournamentConfigJson(tournament.type, config),
                                )
                                Api.updateTournament(token, tournament.id, input)
                                saved = true
                                onSaved(
                                    tournament.copy(
                                        title = title.trim(), slug = slug, status = status,
                                        startsAt = startsAt.ifBlank { null }, endsAt = endsAt.takeIf { it.isNotBlank() }, config = config,
                                    )
                                )
                            } catch (e: Throwable) {
                                error = "Uložení selhalo: ${e.apiMessage()}"
                            }
                            saving = false
                        }
                    }
                }) { Text(if (saving) "Ukládám…" else "Uložit nastavení") }
            }
        }
    }
}

/** Popisná stránka turnaje — stejný editor jako Obsah webu (spec 10.1 / 17.9). */
@Composable
private fun TournamentDescriptionTab(
    token: String?,
    tournament: TournamentDto,
    canEdit: Boolean,
    onSaved: (String) -> Unit,
) {
    var html by remember(tournament.id) { mutableStateOf(tournament.bodyHtml ?: "") }
    var saving by remember(tournament.id) { mutableStateOf(false) }
    var saved by remember(tournament.id) { mutableStateOf(false) }
    var error by remember(tournament.id) { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div {
        H3 { Text("Popis turnaje") }
        if (canEdit) {
            P(attrs = { classes("muted") }) {
                Text("Sem patří i výhry — do databáze se nezanášejí, uvádějí se textem tady (spec 17.9).")
            }
            ContentEditor(token = token, initialHtml = tournament.bodyHtml ?: "") { html = it; saved = false }
            error?.let { Div(attrs = { classes("editor-error") }) { Text("Chyba: $it") } }
            if (saved) Div { Text("Uloženo.") }
            Div(attrs = { classes("save-action") }) {
                Button(attrs = {
                    if (saving) disabled()
                    onClick {
                        saving = true
                        error = null
                        scope.launch {
                            try {
                                Api.updateTournamentBody(token, tournament.id, html)
                                saved = true
                                onSaved(html)
                            } catch (e: Throwable) {
                                error = "Uložení selhalo: ${e.apiMessage()}"
                            }
                            saving = false
                        }
                    }
                }) { Text(if (saving) "Ukládám…" else "Uložit popis") }
            }
        } else {
            RichContent(tournament.bodyHtml ?: "")
        }
    }
}

/** Číselné pole se stejnou konvencí jako skóre — jen číslice, prázdné se bere jako beze změny. */
@Composable
fun NumberField(label: String, value: Int, enabled: Boolean, onChange: (Int) -> Unit) {
    val fieldId = "tournament-number-${slugify(label)}"
    Div(attrs = { classes("tournament-setting-row") }) {
        Label(attrs = { attr("for", fieldId) }) { Text("$label:") }
        Input(type = InputType.Text) {
            id(fieldId)
            if (!enabled) disabled()
            value(value.toString())
            onInput { ev ->
                val digits = ev.value.filter { it.isDigit() }.take(4)
                digits.toIntOrNull()?.let { onChange(it) }
            }
        }
    }
}

@Composable
fun CheckField(label: String, checked: Boolean, enabled: Boolean, onChange: (Boolean) -> Unit) {
    val fieldId = "tournament-check-${slugify(label)}"
    Div(attrs = { classes("tournament-setting-row") }) {
        Label(attrs = { attr("for", fieldId) }) { Text("$label:") }
        CheckboxInput(checked = checked) {
            id(fieldId)
            if (!enabled) disabled()
            onInput { onChange(it.value) }
        }
    }
}
